import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { apiGet } from '../../lib/apiClient';

export default function SymbolPerformanceBreakdown({ symbolStats = [], eaStats = [], accountId = 'all', typeFilter = 'all' }) {
  const [eaBreakdown, setEaBreakdown] = useState({});

  useEffect(() => {
    const params = new URLSearchParams();
    if (accountId !== 'all') {
      params.set('account', accountId);
    } else if (typeFilter !== 'all') {
      params.set('type_filter', typeFilter);
    }
    const aqa = params.toString() ? `?${params.toString()}` : '';
    apiGet(`/api/reports/by-symbol-ea-breakdown${aqa}`)
      .then(setEaBreakdown)
      .catch(() => setEaBreakdown({}));
  }, [accountId, typeFilter]);

  if (!symbolStats || symbolStats.length === 0) {
    return <p className="text-[#484f58] text-sm text-center py-4">No symbol data yet.</p>;
  }

  const maxProfit = Math.max(...symbolStats.map(s => Math.abs(s.net_profit)), 1);

  return (
    <div className="space-y-6">
      {/* Progress bars */}
      <div className="space-y-3">
        {symbolStats.map(sym => {
          const width = (Math.abs(sym.net_profit) / maxProfit) * 100;
          return (
            <div key={sym.symbol} className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="text-[#e6edf3] text-sm font-semibold w-[80px] sm:w-[110px] truncate">{sym.symbol}</span>
              <div className="flex-1 min-w-[80px] h-6 bg-[#0d1117] rounded-md overflow-hidden order-last sm:order-none w-full sm:w-auto">
                <div
                  className="h-full rounded-md"
                  style={{
                    width: `${width}%`,
                    background: sym.net_profit >= 0
                      ? 'linear-gradient(90deg, #1a7a4a 0%, #00ff88 100%)'
                      : 'linear-gradient(90deg, #7a1a1a 0%, #ff4757 100%)',
                  }}
                />
              </div>
              <span className={`text-sm font-semibold w-[70px] sm:w-[90px] text-right ${sym.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                {sym.net_profit >= 0 ? '+' : '-'}${Math.abs(sym.net_profit).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </span>
              <span className="text-[#8b949e] text-xs w-[60px] sm:w-[80px] text-right">{sym.total_trades} trades</span>
            </div>
          );
        })}
      </div>

      {/* Symbol cards with EA breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {symbolStats.map((sym, i) => (
          <SymbolCard key={sym.symbol} symbol={sym} isTop={i === 0} eaBreakdownForSymbol={eaBreakdown[sym.symbol] || []} />
        ))}
      </div>
    </div>
  );
}

function SymbolCard({ symbol, isTop, eaBreakdownForSymbol }) {
  const [expanded, setExpanded] = useState(false);

  const breakdown = eaBreakdownForSymbol || [];

  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[#e6edf3] text-base font-bold truncate">{symbol.symbol}</span>
          {isTop && (
            <span className="bg-[#f59e0b] text-[#0d1117] text-[8px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">TOP</span>
          )}
        </div>
        <span className={`text-base font-bold whitespace-nowrap flex-shrink-0 ${symbol.net_profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
          {symbol.net_profit >= 0 ? '+' : ''}${Math.abs(symbol.net_profit).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-1 text-center mb-3 leading-tight">
        <div>
          <p className="text-[#e6edf3] text-sm font-bold">{symbol.total_trades}</p>
          <p className="text-[#8b949e] text-[9px] uppercase">Trades</p>
        </div>
        <div>
          <p className="text-[#e6edf3] text-sm font-bold">{symbol.wins}</p>
          <p className="text-[#8b949e] text-[9px] uppercase">Wins</p>
        </div>
        <div>
          <p className="text-[#00ff88] text-sm font-bold">{symbol.win_rate}%</p>
          <p className="text-[#8b949e] text-[9px] uppercase">Win Rate</p>
        </div>
      </div>

      {/* Win rate bar */}
      <div className="w-full h-1 bg-[#21262d] rounded-full overflow-hidden mb-3">
        <div className="h-full bg-[#00ff88] rounded-full" style={{ width: `${symbol.win_rate}%` }} />
      </div>

      {/* EA Breakdown toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[#8b949e] text-[11px] hover:text-[#e6edf3] w-full"
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        EA BREAKDOWN ({breakdown.length})
      </button>

      {/* EA breakdown list */}
      {expanded && (
        <div className="mt-2 space-y-1.5 border-t border-[#21262d] pt-2">
          {breakdown.map((ea, i) => (
            <div key={i} className="flex items-center justify-between text-[11px]">
              <span className="text-[#e6edf3] truncate max-w-[100px]">{ea.ea_name}</span>
              <div className="flex items-center gap-1">
                <span className={`font-semibold ${ea.profit >= 0 ? 'text-[#00ff88]' : 'text-[#ff4757]'}`}>
                  ${Math.abs(ea.profit).toLocaleString(undefined, {minimumFractionDigits: 2})}
                </span>
                <span className="text-[#484f58]">({ea.trades})</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
